import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Armchair,
  Blinds,
  Check,
  Circle,
  Cylinder,
  DoorOpen,
  Layers,
  LayoutPanelLeft,
  MousePointer2,
  MousePointerClick,
  PaintBucket,
  PenLine,
  Plus,
  RectangleHorizontal,
  Save,
  Square,
  Trash2,
  Undo2,
} from "lucide-react";

import { useCart, type Table, type TableShape } from "@/components/dashboard/cart-context";
import { FloorGeometryLayer, type GeometrySelection } from "@/components/dashboard/floor-geometry";
import {
  axisSnap,
  distance,
  EMPTY_LAYOUT,
  nearestOnWalls,
  pointInPolygon,
  polygonCentroid,
  wallSegments,
  ZONE_PALETTE,
  type FloorLayout,
  type FloorZone,
  type Point,
  type WallPlacement,
} from "@/components/dashboard/floor-layout";
import { TableVisual } from "@/components/dashboard/table-visual";

export const Route = createFileRoute("/_auth/admin/floor-plan")({
  component: FloorPlanEditor,
});

type Mode = "select" | "wall" | "zone" | "door" | "window" | "column" | "table" | "sofa";

type Selection = { kind: "table"; id: string } | NonNullable<GeometrySelection> | null;

const shapeOptions: { value: TableShape; label: string; icon: typeof Circle }[] = [
  { value: "round", label: "Yuvarlak", icon: Circle },
  { value: "square", label: "Kare", icon: Square },
  { value: "rect", label: "Uzun", icon: RectangleHorizontal },
  { value: "l", label: "L Masa", icon: LayoutPanelLeft },
];

const tools: { mode: Mode; label: string; icon: typeof Circle }[] = [
  { mode: "select", label: "Seç & Taşı", icon: MousePointer2 },
  { mode: "wall", label: "Duvar / Sınır Çiz", icon: PenLine },
  { mode: "zone", label: "Zemin Bölgesi Boya", icon: PaintBucket },
  { mode: "table", label: "Masa Çiz", icon: LayoutPanelLeft },
  { mode: "sofa", label: "Koltuk Çiz", icon: Armchair },
  { mode: "door", label: "Kapı Yerleştir", icon: DoorOpen },
  { mode: "window", label: "Pencere Yerleştir", icon: Blinds },
  { mode: "column", label: "Kolon Yerleştir", icon: Cylinder },
];

const modeHints: Record<Mode, string> = {
  select: "Seçmek için tıklayın, taşımak için sürükleyin",
  wall: "Duvar köşelerini tıklayın • İlk noktaya tıklayınca kapanır • Enter: bitir • Esc: iptal",
  zone: "Bölge köşelerini tıklayın • İlk noktaya tıklayınca kapanır • Esc: iptal",
  table: "Masa köşelerini tıklayın • İlk noktaya tıklayınca kapanır • Enter: bitir • Esc: iptal",
  sofa: "Koltuk köşelerini tıklayın • İlk noktaya tıklayınca kapanır • Enter: bitir • Esc: iptal",
  door: "Kapıyı yerleştirmek için bir duvarın üzerine tıklayın",
  window: "Pencereyi yerleştirmek için bir duvarın üzerine tıklayın",
  column: "Kolon yerleştirmek için zemine tıklayın",
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function FloorPlanEditor() {
  const cart = useCart();
  const canvasRef = useRef<HTMLDivElement>(null);

  const [activeFloor, setActiveFloor] = useState("Zemin Kat");
  const [mode, setMode] = useState<Mode>("select");
  const [selection, setSelection] = useState<Selection>(null);
  const [draftPoints, setDraftPoints] = useState<Point[]>([]);
  const [hoverPoint, setHoverPoint] = useState<Point | null>(null);
  const [ghost, setGhost] = useState<WallPlacement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });

  // Save button feedback, which falls back to the dirty/clean state on its own.
  const [saveState, setSaveState] = useState<"idle" | "saved" | "error">("idle");
  const saveTimer = useRef<number | null>(null);

  function handleSave() {
    setSaveState(cart.saveFloorPlan() ? "saved" : "error");
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => setSaveState("idle"), 2000);
  }

  useEffect(() => {
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, []);

  // Geometry undo stack (per floor, cleared on floor switch).
  const undoStack = useRef<FloorLayout[]>([]);
  const lastUndoPush = useRef(0);
  const [undoCount, setUndoCount] = useState(0);

  const layout = cart.floorLayouts[activeFloor] ?? EMPTY_LAYOUT;
  const floorWidth = layout.width ?? 20;
  const floorHeight = layout.height ?? 15;

  function snapToFloorGrid(p: Point): Point {
    const gridX = 100 / floorWidth;
    const gridY = 100 / floorHeight;
    return {
      x: Math.round(p.x / gridX) * gridX,
      y: Math.round(p.y / gridY) * gridY,
    };
  }

  const floors = useMemo(() => {
    return Array.from(new Set(cart.tables.map((t) => t.floor)));
  }, [cart.tables]);

  const floorTables = cart.tables.filter((t) => t.floor === activeFloor);
  const selectedTable =
    selection?.kind === "table" ? (cart.tables.find((t) => t.id === selection.id) ?? null) : null;
  const selectedZone =
    selection?.kind === "zone" ? (layout.zones.find((z) => z.id === selection.id) ?? null) : null;
  const selectedWall =
    selection?.kind === "wall" ? (layout.walls.find((w) => w.id === selection.id) ?? null) : null;
  const selectedOpening =
    selection?.kind === "opening"
      ? (layout.openings.find((o) => o.id === selection.id) ?? null)
      : null;
  const selectedColumn =
    selection?.kind === "column"
      ? (layout.columns.find((c) => c.id === selection.id) ?? null)
      : null;
  const selectedSofa =
    selection?.kind === "sofa"
      ? ((layout.sofas ?? []).find((s) => s.id === selection.id) ?? null)
      : null;
  const areasOnFloor = Array.from(new Set(floorTables.map((t) => t.area)));

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setCanvasSize({ w: rect.width, h: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toPx = (p: Point): Point => ({
    x: (p.x / 100) * canvasSize.w,
    y: (p.y / 100) * canvasSize.h,
  });

  function eventToPercent(e: { clientX: number; clientY: number }): Point {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 50, y: 50 };
    return {
      x: clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100),
      y: clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100),
    };
  }

  // Commit a layout change; batch rapid changes (typing, sliders) into one
  // undo entry by only snapshotting when the last push is >400ms old.
  function commitLayout(next: FloorLayout) {
    const now = Date.now();
    if (now - lastUndoPush.current > 400) {
      undoStack.current.push(layout);
      if (undoStack.current.length > 50) undoStack.current.shift();
      setUndoCount(undoStack.current.length);
    }
    lastUndoPush.current = now;
    cart.setFloorLayout(activeFloor, next);
  }

  function undo() {
    const previous = undoStack.current.pop();
    if (previous) {
      cart.setFloorLayout(activeFloor, previous);
      setUndoCount(undoStack.current.length);
      setSelection(null);
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setDraftPoints([]);
    setHoverPoint(null);
    setGhost(null);
    if (next !== "select") setSelection(null);
  }

  function switchFloor(floor: string) {
    setActiveFloor(floor);
    setSelection(null);
    setDraftPoints([]);
    setGhost(null);
    undoStack.current = [];
    setUndoCount(0);
  }

  // ------------------------------------------------------------------
  // Drawing (wall & zone pen)
  // ------------------------------------------------------------------

  const canCloseDraft = draftPoints.length >= 3;

  function currentSnappedPoint(raw: Point): Point {
    let p = snapToFloorGrid(raw);
    if (draftPoints.length > 0) p = axisSnap(draftPoints[draftPoints.length - 1], p);
    return p;
  }

  function finishDraft(closed: boolean) {
    if (mode === "wall" && draftPoints.length >= 2) {
      commitLayout({
        ...layout,
        walls: [...layout.walls, { id: `wall-${Date.now()}`, points: draftPoints, closed }],
      });
    } else if (mode === "zone" && draftPoints.length >= 3) {
      const zone: FloorZone = {
        id: `zone-${Date.now()}`,
        name: `Bölge ${layout.zones.length + 1}`,
        color: ZONE_PALETTE[layout.zones.length % ZONE_PALETTE.length].value,
        points: draftPoints,
      };
      commitLayout({ ...layout, zones: [...layout.zones, zone] });
      // The zone doubles as an Area — adopt the tables inside it.
      floorTables.forEach((t) => {
        if (pointInPolygon({ x: t.x, y: t.y }, zone.points)) {
          cart.updateTable(t.id, { area: zone.name });
        }
      });
      setMode("select");
      setSelection({ kind: "zone", id: zone.id });
    } else if (mode === "sofa" && draftPoints.length >= 3) {
      const sofa = {
        id: `sofa-${Date.now()}`,
        points: draftPoints,
        closed,
      };
      commitLayout({
        ...layout,
        sofas: [...(layout.sofas ?? []), sofa],
      });
      setMode("select");
      setSelection({ kind: "sofa", id: sofa.id });
    } else if (mode === "table" && draftPoints.length >= 3) {
      const id = `custom-${Date.now()}`;
      const centroid = polygonCentroid(draftPoints);
      cart.addTable({
        id,
        name: `M-${String(floorTables.length + 1).padStart(2, "0")}`,
        floor: activeFloor,
        area: areasOnFloor[0] ?? "Ana Salon",
        status: "available",
        x: Math.round(centroid.x),
        y: Math.round(centroid.y),
        shape: "custom",
        points: draftPoints,
      });
      setMode("select");
      setSelection({ kind: "table", id });
    }
    setDraftPoints([]);
    setHoverPoint(null);
  }

  function handleDrawPointerDown(e: React.PointerEvent) {
    const raw = eventToPercent(e);

    if (mode === "wall" || mode === "zone" || mode === "sofa" || mode === "table") {
      const p = currentSnappedPoint(raw);
      if (canCloseDraft && distance(p, draftPoints[0]) < 2.5) {
        finishDraft(true);
        return;
      }
      setDraftPoints([...draftPoints, p]);
      return;
    }

    if (mode === "door" || mode === "window") {
      if (!ghost) return;
      commitLayout({
        ...layout,
        openings: [
          ...layout.openings,
          {
            id: `opening-${Date.now()}`,
            type: mode,
            wallId: ghost.wallId,
            segment: ghost.segment,
            t: ghost.t,
            width: mode === "door" ? 7 : 10,
          },
        ],
      });
      return;
    }

    if (mode === "column") {
      const p = snapToFloorGrid(raw);
      commitLayout({
        ...layout,
        columns: [
          ...layout.columns,
          { id: `column-${Date.now()}`, x: p.x, y: p.y, size: 2.6, shape: "square" },
        ],
      });
    }
  }

  function handleDrawPointerMove(e: React.PointerEvent) {
    const raw = eventToPercent(e);
    if (mode === "door" || mode === "window") {
      setGhost(nearestOnWalls(raw, layout.walls, 5));
      setHoverPoint(null);
    } else {
      setHoverPoint(currentSnappedPoint(raw));
      setGhost(null);
    }
  }

  // ------------------------------------------------------------------
  // Selection deletion & keyboard shortcuts
  // ------------------------------------------------------------------

  const selectedTableHasOrder =
    selectedTable != null && (cart.tablesOrderItems[selectedTable.id] ?? []).length > 0;

  function deleteSelection() {
    if (!selection) return;
    if (selection.kind === "table") {
      if (selectedTableHasOrder) return;
      cart.removeTable(selection.id);
    } else if (selection.kind === "wall") {
      commitLayout({
        ...layout,
        walls: layout.walls.filter((w) => w.id !== selection.id),
        openings: layout.openings.filter((o) => o.wallId !== selection.id),
      });
    } else if (selection.kind === "zone") {
      commitLayout({ ...layout, zones: layout.zones.filter((z) => z.id !== selection.id) });
    } else if (selection.kind === "opening") {
      commitLayout({ ...layout, openings: layout.openings.filter((o) => o.id !== selection.id) });
    } else if (selection.kind === "column") {
      commitLayout({ ...layout, columns: layout.columns.filter((c) => c.id !== selection.id) });
    } else if (selection.kind === "sofa") {
      commitLayout({ ...layout, sofas: (layout.sofas ?? []).filter((s) => s.id !== selection.id) });
    }
    setSelection(null);
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const isTyping = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

      if (e.key === "Escape") {
        if (draftPoints.length > 0) setDraftPoints([]);
        else if (mode !== "select") switchMode("select");
        else setSelection(null);
      } else if (e.key === "Enter" && (mode === "wall" || mode === "zone" || mode === "table" || mode === "sofa")) {
        finishDraft(mode === "zone" || mode === "table" || mode === "sofa");
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && !isTyping) {
        deleteSelection();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // ------------------------------------------------------------------
  // Tables (select mode)
  // ------------------------------------------------------------------

  function startTableDrag(e: React.PointerEvent, table: Table) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    setSelection({ kind: "table", id: table.id });
    const rect = canvas.getBoundingClientRect();
    const zones = layout.zones;

    const initialX = table.x;
    const initialY = table.y;
    const clickXPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const clickYPercent = ((e.clientY - rect.top) / rect.height) * 100;

    const handleMove = (ev: PointerEvent) => {
      const currentXPercent = ((ev.clientX - rect.left) / rect.width) * 100;
      const currentYPercent = ((ev.clientY - rect.top) / rect.height) * 100;
      const dx = currentXPercent - clickXPercent;
      const dy = currentYPercent - clickYPercent;

      if (table.points) {
        const nextPoints = table.points.map((p) => ({
          x: clamp(p.x + dx, 0, 100),
          y: clamp(p.y + dy, 0, 100),
        }));
        const centroid = polygonCentroid(nextPoints);
        cart.updateTable(table.id, {
          x: Math.round(centroid.x),
          y: Math.round(centroid.y),
          points: nextPoints,
        });
      } else {
        const x = clamp(initialX + dx, 5, 95);
        const y = clamp(initialY + dy, 8, 92);
        cart.updateTable(table.id, { x: Math.round(x), y: Math.round(y) });
      }
    };
    const handleUp = (ev: PointerEvent) => {
      // Dropping a table inside a zone adopts that zone as its area.
      const currentXPercent = ((ev.clientX - rect.left) / rect.width) * 100;
      const currentYPercent = ((ev.clientY - rect.top) / rect.height) * 100;
      const dx = currentXPercent - clickXPercent;
      const dy = currentYPercent - clickYPercent;
      const x = clamp(initialX + dx, 5, 95);
      const y = clamp(initialY + dy, 8, 92);

      const zone = zones.find((z) => pointInPolygon({ x, y }, z.points));
      if (zone && zone.name !== table.area) {
        cart.updateTable(table.id, { area: zone.name });
      }
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }

  function handleAddTable() {
    const id = `custom-${Date.now()}`;
    cart.addTable({
      id,
      name: `M-${String(floorTables.length + 1).padStart(2, "0")}`,
      floor: activeFloor,
      area: areasOnFloor[0] ?? "Ana Salon",
      status: "available",
      x: 50,
      y: 50,
      shape: "square",
    });
    switchMode("select");
    setSelection({ kind: "table", id });
  }

  function handleAddFloor() {
    const existingNumbers = floors
      .map((f) => parseInt(f.replace(/\D/g, ""), 10))
      .filter((n) => !Number.isNaN(n));
    const nextNumber = (existingNumbers.length ? Math.max(...existingNumbers) : 0) + 1;
    const floorName = `${nextNumber}. Kat`;
    const id = `custom-${Date.now()}`;
    cart.addTable({
      id,
      name: "M-01",
      floor: floorName,
      area: "Ana Salon",
      status: "available",
      x: 50,
      y: 50,
      shape: "square",
    });
    switchFloor(floorName);
    setSelection({ kind: "table", id });
  }

  function renameZone(zone: FloorZone, name: string) {
    const oldName = zone.name;
    commitLayout({
      ...layout,
      zones: layout.zones.map((z) => (z.id === zone.id ? { ...z, name } : z)),
    });
    cart.tables
      .filter((t) => t.floor === activeFloor && t.area === oldName)
      .forEach((t) => cart.updateTable(t.id, { area: name }));
  }

  // Ghost door/window preview geometry (px space)
  const ghostRender = useMemo(() => {
    if (!ghost) return null;
    const wall = layout.walls.find((w) => w.id === ghost.wallId);
    if (!wall) return null;
    const segment = wallSegments(wall).find((s) => s.index === ghost.segment);
    if (!segment) return null;
    const a = toPx(segment.a);
    const b = toPx(segment.b);
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length === 0) return null;
    const dir = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
    const point = toPx(ghost.point);
    const w = (((mode === "door" ? 7 : 10) / 100) * (canvasSize.w + canvasSize.h)) / 2;
    return { point, dir, w };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghost, layout.walls, mode, canvasSize]);

  const draftPreviewColor =
    mode === "zone"
      ? ZONE_PALETTE[layout.zones.length % ZONE_PALETTE.length].value
      : mode === "sofa"
      ? "rgba(219, 234, 254, 0.45)"
      : mode === "table"
      ? "rgba(244, 244, 245, 0.6)"
      : null;

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900">
            Salon & Masa Yönetimi
          </h1>
          <p className="text-sm text-gray-500">
            Dükkanınızı 2D olarak çizin: sınırlar, duvarlar, kapılar, zemin renkleri ve masalar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Every edit is live in memory but only reaches storage on save, so
              say out loud when the drawing is ahead of what's kept. */}
          {cart.isFloorPlanDirty && saveState === "idle" && (
            <span className="flex items-center gap-1.5 pr-1 text-[11px] font-bold text-amber-600">
              <span className="size-1.5 rounded-full bg-amber-500" />
              Kaydedilmemiş değişiklik
            </span>
          )}
          {saveState === "error" && (
            <span className="pr-1 text-[11px] font-bold text-rose-600">
              Kaydedilemedi — tarayıcı deposu engelli olabilir
            </span>
          )}
          <button
            onClick={handleAddTable}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs font-extrabold uppercase tracking-wide text-gray-700 shadow-sm transition-all hover:bg-gray-50"
          >
            <Plus className="size-4" />
            <span>Yeni Masa Ekle</span>
          </button>
          <button
            onClick={handleSave}
            disabled={!cart.isFloorPlanDirty}
            className={`flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-xs font-extrabold uppercase tracking-wide shadow-sm transition-all ${
              saveState === "saved"
                ? "bg-emerald-600 text-white"
                : "bg-gray-900 text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            }`}
          >
            {saveState === "saved" ? <Check className="size-4" /> : <Save className="size-4" />}
            <span>{saveState === "saved" ? "Kaydedildi" : "Kaydet"}</span>
          </button>
        </div>
      </div>

      {/* Floor pills */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm shrink-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="px-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">
            Katlar
          </span>
          {floors.map((floor) => (
            <button
              key={floor}
              onClick={() => switchFloor(floor)}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold shadow-sm transition-all ${
                activeFloor === floor
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200/80"
              }`}
            >
              <Layers className="size-3.5" />
              <span>{floor}</span>
            </button>
          ))}
          <button
            onClick={handleAddFloor}
            className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-3.5 py-1.5 text-xs font-semibold text-gray-500 transition-all hover:border-gray-400 hover:text-gray-700"
          >
            <Plus className="size-3.5" />
            <span>Kat Ekle</span>
          </button>
        </div>

        <div className="h-6 w-px bg-gray-200 mx-1" />
        <span className="px-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">
          Tuval Boyutu (Metre)
        </span>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1">
            <span className="text-[10px] font-bold text-gray-400 uppercase">En</span>
            <input
              type="number"
              min="5"
              max="100"
              value={layout.width ?? 20}
              onChange={(e) => {
                const val = Math.max(5, parseInt(e.target.value) || 20);
                cart.setFloorLayout(activeFloor, { ...layout, width: val });
              }}
              className="w-12 bg-transparent text-center text-xs font-bold text-gray-700 focus:outline-none"
            />
            <span className="text-[10px] font-semibold text-gray-400">m</span>
          </div>

          <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1">
            <span className="text-[10px] font-bold text-gray-400 uppercase">Boy</span>
            <input
              type="number"
              min="5"
              max="100"
              value={layout.height ?? 15}
              onChange={(e) => {
                const val = Math.max(5, parseInt(e.target.value) || 15);
                cart.setFloorLayout(activeFloor, { ...layout, height: val });
              }}
              className="w-12 bg-transparent text-center text-xs font-bold text-gray-700 focus:outline-none"
            />
            <span className="text-[10px] font-semibold text-gray-400">m</span>
          </div>
        </div>
      </div>

      {/* Editor body */}
      <div className="flex flex-1 gap-4 min-h-0">
        {/* Canvas & Toolbar Wrapper */}
        <div className="flex-1 flex flex-col gap-4 min-w-0 h-full">
          {/* Canvas */}
          <div className="relative flex-1 min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50/50 flex items-center justify-center p-4">
            <div
              ref={canvasRef}
              className="relative border border-gray-200 bg-[#fafaf8] shadow-sm rounded-2xl overflow-hidden"
              style={{
                aspectRatio: `${floorWidth} / ${floorHeight}`,
                width: "100%",
                height: "100%",
                maxWidth: "100%",
                maxHeight: "100%",
              }}
              onPointerDown={(e) => {
                if (mode === "select" && e.target === e.currentTarget) setSelection(null);
              }}
            >
              {/* Floor texture */}
              <div
                className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px)]"
                style={{
                  backgroundSize: `${100 / floorWidth}% ${100 / floorHeight}%`,
                }}
              />

              {/* Drawn geometry */}
              <FloorGeometryLayer
                layout={layout}
                tables={floorTables}
                tableAdisyonlar={cart.tableAdisyonlar}
                selection={selection}
                interactive={mode === "select"}
                onSelect={(sel, e) => {
                  setSelection(sel);
                  if (sel.kind === "table") {
                    const table = floorTables.find((t) => t.id === sel.id);
                    if (table) startTableDrag(e, table);
                  }
                }}
              />

              {/* Standard (non-custom) Tables */}
              {floorTables.filter((t) => t.shape !== "custom" && !t.points).map((table) => {
                const isSelected = selection?.kind === "table" && selection.id === table.id;
                return (
                  <div
                    key={table.id}
                    onPointerDown={mode === "select" ? (e) => startTableDrag(e, table) : undefined}
                    style={{
                      left: `${table.x}%`,
                      top: `${table.y}%`,
                      transform: `translate(-50%, -50%) scale(${(24 * (table.scale ?? 1)) / floorWidth})`,
                    }}
                    className={`absolute touch-none rounded-xl p-1 transition-transform ${
                      isSelected ? "z-20 cursor-grabbing" : "z-10 cursor-grab"
                    } ${mode !== "select" ? "pointer-events-none" : ""}`}
                  >
                    <div
                      className={`transition-all duration-200 ${isSelected ? "scale-105" : "hover:scale-103"}`}
                    >
                      <TableVisual
                        name={table.name}
                        status={table.status}
                        shape={table.shape}
                        splitCount={(cart.tableAdisyonlar[table.id] ?? []).length || 1}
                        isSelected={isSelected}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Drawing overlay: captures pointer input in draw modes */}
              {mode !== "select" && (
                <div
                  className="absolute inset-0 z-30 cursor-crosshair touch-none"
                  onPointerDown={handleDrawPointerDown}
                  onPointerMove={handleDrawPointerMove}
                  onPointerLeave={() => {
                    setHoverPoint(null);
                    setGhost(null);
                  }}
                >
                  <svg
                    width={canvasSize.w}
                    height={canvasSize.h}
                    className="pointer-events-none absolute inset-0 overflow-visible"
                  >
                    {/* Zone fill preview */}
                    {draftPreviewColor && draftPoints.length >= 2 && hoverPoint && (
                      <polygon
                        points={[...draftPoints, hoverPoint]
                          .map((p) => `${toPx(p).x},${toPx(p).y}`)
                          .join(" ")}
                        fill={draftPreviewColor}
                        fillOpacity={0.55}
                        stroke="none"
                      />
                    )}

                    {/* Draft polyline */}
                    {draftPoints.length > 0 && (
                      <polyline
                        points={draftPoints.map((p) => `${toPx(p).x},${toPx(p).y}`).join(" ")}
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                      />
                    )}

                    {/* Live segment to cursor */}
                    {draftPoints.length > 0 && hoverPoint && (
                      <line
                        x1={toPx(draftPoints[draftPoints.length - 1]).x}
                        y1={toPx(draftPoints[draftPoints.length - 1]).y}
                        x2={toPx(hoverPoint).x}
                        y2={toPx(hoverPoint).y}
                        stroke="#3b82f6"
                        strokeWidth={1.5}
                        strokeDasharray="5 4"
                      />
                    )}

                    {/* Draft vertices */}
                    {draftPoints.map((p, i) => {
                      const px = toPx(p);
                      const isFirst = i === 0;
                      const closable =
                        isFirst && canCloseDraft && hoverPoint && distance(hoverPoint, p) < 2.5;
                      return (
                        <circle
                          key={i}
                          cx={px.x}
                          cy={px.y}
                          r={closable ? 7 : 3.5}
                          fill={closable ? "#22c55e" : "#ffffff"}
                          stroke={closable ? "#16a34a" : "#3b82f6"}
                          strokeWidth={2}
                        />
                      );
                    })}

                    {/* Cursor crosshair dot */}
                    {hoverPoint && (mode === "wall" || mode === "zone" || mode === "column" || mode === "sofa") && (
                      <circle
                        cx={toPx(hoverPoint).x}
                        cy={toPx(hoverPoint).y}
                        r={mode === "column" ? 9 : 4}
                        fill={mode === "column" ? "rgba(87,83,78,0.35)" : "rgba(59,130,246,0.35)"}
                        stroke={mode === "column" ? "#57534e" : "#3b82f6"}
                        strokeWidth={1.5}
                      />
                    )}

                    {/* Ghost door/window on nearest wall */}
                    {ghostRender && (
                      <g>
                        <line
                          x1={ghostRender.point.x - ghostRender.dir.x * (ghostRender.w / 2)}
                          y1={ghostRender.point.y - ghostRender.dir.y * (ghostRender.w / 2)}
                          x2={ghostRender.point.x + ghostRender.dir.x * (ghostRender.w / 2)}
                          y2={ghostRender.point.y + ghostRender.dir.y * (ghostRender.w / 2)}
                          stroke="#3b82f6"
                          strokeWidth={7}
                          strokeLinecap="butt"
                          opacity={0.55}
                        />
                        <circle
                          cx={ghostRender.point.x}
                          cy={ghostRender.point.y}
                          r={5}
                          fill="#3b82f6"
                          opacity={0.7}
                        />
                      </g>
                    )}
                  </svg>
                </div>
              )}
            </div>

            {/* Hint pill */}
            <div className="pointer-events-none absolute bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-gray-200 bg-white/90 px-3 py-1.5 text-[11px] font-semibold text-gray-500 shadow-sm backdrop-blur-sm">
              <MousePointerClick className="size-3.5" />
              <span>{modeHints[mode]}</span>
            </div>
          </div>

          {/* Horizontal Designer Toolbar */}
          <div className="rounded-2xl border border-gray-200 bg-white p-2.5 shadow-sm shrink-0 flex items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {tools.map((tool) => {
                const Icon = tool.icon;
                const isActive = mode === tool.mode;
                return (
                  <button
                    key={tool.mode}
                    title={tool.label}
                    onClick={() => switchMode(tool.mode)}
                    className={`flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-bold transition-all shadow-sm ${
                      isActive
                        ? "bg-gray-900 text-white"
                        : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="size-4" />
                    <span>{tool.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <button
                title="Geri Al (Ctrl+Z)"
                onClick={undo}
                disabled={undoCount === 0}
                className="flex size-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition-all hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent shadow-sm"
              >
                <Undo2 className="size-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Properties panel */}
        <aside className="w-[300px] shrink-0 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          {selectedTable ? (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black tracking-tight text-gray-900">
                    Masa {selectedTable.name}
                  </h2>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase text-gray-400">
                    {selectedTable.floor} • Konum %{selectedTable.x} / %{selectedTable.y}
                  </p>
                </div>
                <div className="flex size-9 items-center justify-center rounded-xl bg-gray-50 text-gray-900">
                  <Armchair className="size-4.5" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Masa Adı
                </label>
                <input
                  type="text"
                  value={selectedTable.name}
                  onChange={(e) => cart.updateTable(selectedTable.id, { name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800 focus:border-gray-400 focus:bg-white focus:outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Masa Şekli
                </label>
                <div className="flex gap-1.5">
                  {shapeOptions.map((shape) => {
                    const Icon = shape.icon;
                    const isActive = selectedTable.shape === shape.value;
                    return (
                      <button
                        key={shape.value}
                        onClick={() => cart.updateTable(selectedTable.id, { shape: shape.value })}
                        className={`flex flex-1 flex-col items-center gap-1 rounded-lg border py-2.5 text-[10px] font-bold transition-all ${
                          isActive
                            ? "border-gray-700 bg-gray-50 text-gray-800"
                            : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        <Icon className="size-4" />
                        <span>{shape.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Masa Boyutu (Ölçek)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (selectedTable.points) {
                        const centroid = polygonCentroid(selectedTable.points);
                        const nextPoints = selectedTable.points.map((p) => ({
                          x: clamp(centroid.x + (p.x - centroid.x) * 0.9, 0, 100),
                          y: clamp(centroid.y + (p.y - centroid.y) * 0.9, 0, 100),
                        }));
                        cart.updateTable(selectedTable.id, { points: nextPoints });
                      } else {
                        const currentScale = selectedTable.scale ?? 1;
                        cart.updateTable(selectedTable.id, { scale: Math.max(0.5, currentScale - 0.1) });
                      }
                    }}
                    className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white py-2 text-xs font-bold text-gray-600 hover:bg-gray-50 cursor-pointer"
                  >
                    Küçült (-10%)
                  </button>
                  <button
                    onClick={() => {
                      if (selectedTable.points) {
                        const centroid = polygonCentroid(selectedTable.points);
                        const nextPoints = selectedTable.points.map((p) => ({
                          x: clamp(centroid.x + (p.x - centroid.x) * 1.1, 0, 100),
                          y: clamp(centroid.y + (p.y - centroid.y) * 1.1, 0, 100),
                        }));
                        cart.updateTable(selectedTable.id, { points: nextPoints });
                      } else {
                        const currentScale = selectedTable.scale ?? 1;
                        cart.updateTable(selectedTable.id, { scale: Math.min(2.5, currentScale + 0.1) });
                      }
                    }}
                    className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white py-2 text-xs font-bold text-gray-600 hover:bg-gray-50 cursor-pointer"
                  >
                    Büyüt (+10%)
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Alan / Bölge
                </label>
                <input
                  type="text"
                  list="floor-plan-areas"
                  value={selectedTable.area}
                  onChange={(e) => cart.updateTable(selectedTable.id, { area: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800 focus:border-gray-400 focus:bg-white focus:outline-none"
                />
                <datalist id="floor-plan-areas">
                  {areasOnFloor.map((area) => (
                    <option key={area} value={area} />
                  ))}
                </datalist>
                <p className="text-[10px] text-gray-400">
                  Masayı bir bölgenin içine bırakırsanız alanı otomatik atanır.
                </p>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <button
                  onClick={deleteSelection}
                  disabled={selectedTableHasOrder}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-extrabold uppercase tracking-wide transition-all ${
                    selectedTableHasOrder
                      ? "cursor-not-allowed bg-gray-100 text-gray-400"
                      : "bg-rose-50 text-rose-700 hover:bg-rose-100"
                  }`}
                >
                  <Trash2 className="size-3.5" />
                  <span>Masayı Sil</span>
                </button>
              </div>
            </div>
          ) : selectedZone ? (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black tracking-tight text-gray-900">Zemin Bölgesi</h2>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase text-gray-400">
                    {selectedZone.points.length} köşe • Alan tanımlar
                  </p>
                </div>
                <div
                  className="size-9 rounded-xl border border-black/10"
                  style={{ backgroundColor: selectedZone.color }}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Bölge / Alan Adı
                </label>
                <input
                  type="text"
                  value={selectedZone.name}
                  onChange={(e) => renameZone(selectedZone, e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800 focus:border-gray-400 focus:bg-white focus:outline-none"
                />
                <p className="text-[10px] text-gray-400">
                  Ad değişince bu alandaki masalar da yeni ada taşınır.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Zemin Rengi
                </label>
                <div className="grid grid-cols-4 gap-1.5">
                  {ZONE_PALETTE.map((swatch) => (
                    <button
                      key={swatch.value}
                      title={swatch.name}
                      onClick={() =>
                        commitLayout({
                          ...layout,
                          zones: layout.zones.map((z) =>
                            z.id === selectedZone.id ? { ...z, color: swatch.value } : z,
                          ),
                        })
                      }
                      className={`flex h-12 flex-col items-center justify-end rounded-lg border pb-1 text-[9px] font-bold text-gray-500 transition-all ${
                        selectedZone.color === swatch.value
                          ? "border-gray-700 ring-2 ring-gray-300"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      style={{ backgroundColor: swatch.value }}
                    >
                      {swatch.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <button
                  onClick={deleteSelection}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-50 py-2.5 text-xs font-extrabold uppercase tracking-wide text-rose-700 transition-all hover:bg-rose-100"
                >
                  <Trash2 className="size-3.5" />
                  <span>Bölgeyi Sil</span>
                </button>
              </div>
            </div>
          ) : selectedSofa ? (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black tracking-tight text-gray-900">Çizilen Koltuk</h2>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase text-gray-400">
                    {selectedSofa.points.length} Köşe • Özel Oturma Alanı
                  </p>
                </div>
                <div className="flex size-9 items-center justify-center rounded-xl bg-gray-50 text-gray-900">
                  <Armchair className="size-4.5" />
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <button
                  onClick={deleteSelection}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-50 py-2.5 text-xs font-extrabold uppercase tracking-wide text-rose-700 transition-all hover:bg-rose-100"
                >
                  <Trash2 className="size-3.5" />
                  <span>Koltuk Düzenini Sil</span>
                </button>
              </div>
            </div>
          ) : selectedWall ? (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-black tracking-tight text-gray-900">
                  {selectedWall.closed ? "Kapalı Sınır" : "Duvar"}
                </h2>
                <p className="mt-0.5 text-[11px] font-semibold uppercase text-gray-400">
                  {selectedWall.points.length} köşe
                  {selectedWall.closed ? " • Dükkan sınırı" : " • İç duvar"}
                </p>
              </div>
              <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                Duvarı silerseniz üzerindeki kapı ve pencereler de silinir. Şekli değiştirmek için
                silip kalem aracıyla yeniden çizin.
              </p>
              <div className="border-t border-gray-100 pt-4">
                <button
                  onClick={deleteSelection}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-50 py-2.5 text-xs font-extrabold uppercase tracking-wide text-rose-700 transition-all hover:bg-rose-100"
                >
                  <Trash2 className="size-3.5" />
                  <span>Duvarı Sil</span>
                </button>
              </div>
            </div>
          ) : selectedOpening ? (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black tracking-tight text-gray-900">
                    {selectedOpening.type === "door" ? "Kapı" : "Pencere"}
                  </h2>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase text-gray-400">
                    Duvara sabitli
                  </p>
                </div>
                <div className="flex size-9 items-center justify-center rounded-xl bg-gray-50 text-gray-900">
                  {selectedOpening.type === "door" ? (
                    <DoorOpen className="size-4.5" />
                  ) : (
                    <Blinds className="size-4.5" />
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Genişlik ({selectedOpening.width})
                </label>
                <input
                  type="range"
                  min={4}
                  max={16}
                  step={1}
                  value={selectedOpening.width}
                  onChange={(e) =>
                    commitLayout({
                      ...layout,
                      openings: layout.openings.map((o) =>
                        o.id === selectedOpening.id ? { ...o, width: Number(e.target.value) } : o,
                      ),
                    })
                  }
                  className="w-full accent-gray-900"
                />
              </div>

              <div className="border-t border-gray-100 pt-4">
                <button
                  onClick={deleteSelection}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-50 py-2.5 text-xs font-extrabold uppercase tracking-wide text-rose-700 transition-all hover:bg-rose-100"
                >
                  <Trash2 className="size-3.5" />
                  <span>{selectedOpening.type === "door" ? "Kapıyı" : "Pencereyi"} Sil</span>
                </button>
              </div>
            </div>
          ) : selectedColumn ? (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black tracking-tight text-gray-900">Kolon</h2>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase text-gray-400">
                    Konum %{Math.round(selectedColumn.x)} / %{Math.round(selectedColumn.y)}
                  </p>
                </div>
                <div className="flex size-9 items-center justify-center rounded-xl bg-gray-100 text-gray-600">
                  <Cylinder className="size-4.5" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Şekil
                </label>
                <div className="flex gap-1.5">
                  {(
                    [
                      { value: "square", label: "Kare", icon: Square },
                      { value: "round", label: "Yuvarlak", icon: Circle },
                    ] as const
                  ).map((shape) => {
                    const Icon = shape.icon;
                    return (
                      <button
                        key={shape.value}
                        onClick={() =>
                          commitLayout({
                            ...layout,
                            columns: layout.columns.map((c) =>
                              c.id === selectedColumn.id ? { ...c, shape: shape.value } : c,
                            ),
                          })
                        }
                        className={`flex flex-1 flex-col items-center gap-1 rounded-lg border py-2.5 text-[10px] font-bold transition-all ${
                          selectedColumn.shape === shape.value
                            ? "border-gray-700 bg-gray-50 text-gray-800"
                            : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        <Icon className="size-4" />
                        <span>{shape.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Boyut ({selectedColumn.size})
                </label>
                <input
                  type="range"
                  min={1.5}
                  max={5}
                  step={0.5}
                  value={selectedColumn.size}
                  onChange={(e) =>
                    commitLayout({
                      ...layout,
                      columns: layout.columns.map((c) =>
                        c.id === selectedColumn.id ? { ...c, size: Number(e.target.value) } : c,
                      ),
                    })
                  }
                  className="w-full accent-gray-900"
                />
              </div>

              <div className="border-t border-gray-100 pt-4">
                <button
                  onClick={deleteSelection}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-50 py-2.5 text-xs font-extrabold uppercase tracking-wide text-rose-700 transition-all hover:bg-rose-100"
                >
                  <Trash2 className="size-3.5" />
                  <span>Kolonu Sil</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <div className="flex size-12 items-center justify-center rounded-2xl bg-gray-50 text-gray-300">
                <MousePointerClick className="size-6" />
              </div>
              <p className="text-xs font-semibold text-gray-400">
                Düzenlemek için plandan bir öğe seçin
                <br />
                veya soldaki araçlarla çizime başlayın.
              </p>
              <div className="w-full space-y-1.5 rounded-xl border border-gray-100 bg-gray-50/60 p-3 text-left">
                {tools.slice(1).map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <div
                      key={tool.mode}
                      className="flex items-center gap-2 text-[11px] font-semibold text-gray-500"
                    >
                      <Icon className="size-3.5 text-gray-400" />
                      <span>{tool.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
